// src/features/packaging/corrugated/questions/PrintingDetails.jsx
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PerlTextField from "../../../../shared/components/PerlTextField";
import PerlChip from "../../../../shared/components/PerlChip";
import PerlLabelMedium from "../../../../shared/components/PerlLabelMedium";

const PRINTING_OPTIONS = ["Flexography Printing", "Offset Printing", "NA"];

/**
 * PrintingDetails Component
 *
 * Custom question for Corrugated Packaging: captures the printing type and number of colors
 * Uses chip-based selection with read-only text field display
 *
 * @param {string|object} value - Current printing value (string for printing type, or object with printing and numberOfColors)
 * @param {function} onChange - Callback when the printing details change
 * @param {string} [error] - Optional error message
 * @param {string} [success] - Optional success/help message
 */
const PrintingDetails = forwardRef(({ value, onChange, error, success }, ref) => {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    white: "#ffffff",
    textPrimary: "#F1F2F3",
    textSecondary: "#9097A1",
    surface: "#191A1B",
  };
  const brandColor =
    theme.custom?.perlColors?.brandColor || theme.palette?.primary?.main || "#ff6034";

  const [validationError, setValidationError] = useState("");
  const [validationAttempted, setValidationAttempted] = useState(false);

  // Parse the value - can be string (legacy) or object
  let printingValue = "";
  let numberOfColors = "";
  let printingCoverage = "";
  let inkCoverage = "";
  
  if (typeof value === "string") {
    printingValue = value || "";
  } else if (value && typeof value === "object") {
    printingValue = value.printing || "";
    numberOfColors = value.numberOfColors || "";
    printingCoverage = value.printingCoverage || "";
    inkCoverage = value.inkCoverage || "";
  }

  // If printing is "NA", automatically set text fields to 0
  const isNA = printingValue === "NA";
  if (isNA) {
    numberOfColors = 0;
    printingCoverage = 0;
    inkCoverage = 0;
  }

  // Validate printing selection - only allow predefined options
  const validate = () => {
    setValidationAttempted(true);
    if (!printingValue || printingValue.trim() === "") {
      setValidationError("Please select a printing type");
      return false;
    }
    
    const trimmedValue = printingValue.trim();
    const isValid = PRINTING_OPTIONS.some(option => option === trimmedValue);
    
    if (!isValid) {
      setValidationError("Please select a printing type from the options below");
      return false;
    }
    
    setValidationError("");
    return true;
  };

  // Check if all required fields are filled - use current value prop
  const isAllFieldsFilled = () => {
    // Get current value (will be updated on each render)
    let currentValue = {
      printing: "",
      numberOfColors: "",
      printingCoverage: "",
      inkCoverage: "",
    };
    
    // Handle both string (legacy) and object formats
    if (typeof value === "string") {
      currentValue.printing = value || "";
    } else if (value && typeof value === "object") {
      currentValue = {
        printing: value.printing || "",
        numberOfColors: value.numberOfColors || "",
        printingCoverage: value.printingCoverage || "",
        inkCoverage: value.inkCoverage || "",
      };
    }
    
    // Printing is required
    if (!currentValue.printing || currentValue.printing.trim() === "") return false;
    const trimmedPrinting = currentValue.printing.trim();
    if (!PRINTING_OPTIONS.some(option => option === trimmedPrinting)) return false;
    
    // If printing is not "NA", all other fields are required
    if (trimmedPrinting !== "NA") {
      // Number of colors is required
      if (!currentValue.numberOfColors || currentValue.numberOfColors.toString().trim() === "") return false;
      
      // Printing Coverage is required
      if (!currentValue.printingCoverage || currentValue.printingCoverage.toString().trim() === "") return false;
      
      // Ink coverage is required
      if (!currentValue.inkCoverage || currentValue.inkCoverage.toString().trim() === "") return false;
    }
    
    return true;
  };

  // Expose validate method to parent
  useImperativeHandle(ref, () => ({
    validate,
    isValid: isAllFieldsFilled,
  }), [value]); // Add value as dependency to update when it changes

  const handleFieldChange = (field, newValue) => {
    // Use the parsed local variables as the base - they reflect the current render's values
    // These are computed from the value prop at the top of the component
    const updatedValue = {
      printing: field === "printing" ? newValue : printingValue,
      numberOfColors: field === "numberOfColors" ? newValue : numberOfColors,
      printingCoverage: field === "printingCoverage" ? newValue : printingCoverage,
      inkCoverage: field === "inkCoverage" ? newValue : inkCoverage,
    };
    onChange?.(updatedValue);
    // Clear validation error when user makes a change
    if (validationError) {
      setValidationError("");
      setValidationAttempted(false);
    }
  };

  const handlePrintingSelect = (printingType) => {
    // Toggle selection: if already selected, deselect; otherwise select
    const newPrintingValue = printingValue === printingType ? "" : printingType;
    
    // If "NA" is selected, automatically set other fields to 0
    if (newPrintingValue === "NA") {
      const updatedValue = {
        printing: newPrintingValue,
        numberOfColors: 0,
        printingCoverage: 0,
        inkCoverage: 0,
      };
      onChange?.(updatedValue);
      // Clear validation error
      if (validationError) {
        setValidationError("");
        setValidationAttempted(false);
      }
    } else {
      handleFieldChange("printing", newPrintingValue);
    }
  };

  const handleNumberOfColorsChange = (e) => {
    handleFieldChange("numberOfColors", e.target.value);
  };

  const handlePrintingCoverageChange = (e) => {
    handleFieldChange("printingCoverage", e.target.value);
  };

  const handleInkCoverageChange = (e) => {
    handleFieldChange("inkCoverage", e.target.value);
  };
  
  // Prevent typing in the printing text field - only allow chip selection
  const handlePrintingTextChange = (e) => {
    // Don't allow manual typing - only allow chip selection
    // This prevents custom entries
    e.preventDefault();
  };

  return (
    <>
      {/* <PerlLabelMedium sx={{ mb: 2, display: "block" }}>Select Printing Type:</PerlLabelMedium> */}
      <PerlTextField
        fullWidth
        placeholder="Select a printing type from below"
        value={printingValue || ""}
        onChange={handlePrintingTextChange}
        onKeyDown={(e) => {
          // Prevent typing - only allow chip selection
          if (e.key !== "Tab" && e.key !== "Escape") {
            e.preventDefault();
          }
        }}
        inputProps={{
          readOnly: true,
        }}
        error={Boolean(error || validationError)}
        helperText={error || validationError}
      />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mt: 1.5,
          mb: 2,
        }}
      >
        {PRINTING_OPTIONS.map((printingType) => (
          <Box key={printingType} sx={{ width: "auto", flexShrink: 0 }}>
            <PerlChip
              label={printingType}
              onClick={() => handlePrintingSelect(printingType)}
              selected={printingValue === printingType}
            />
          </Box>
        ))}
      </Box>
      {/* <PerlLabelMedium sx={{ mb: 2, display: "block" }}>No of colors:</PerlLabelMedium> */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
        <PerlTextField
          label="No of colors"
          type="number"
          value={numberOfColors || ""}
          onChange={handleNumberOfColorsChange}
          disabled={isNA}
          InputProps={{
            sx: {
              bgcolor: alpha(perlColors.white, 0.02),
              color: perlColors.textPrimary,
              fontSize: "0.9rem",
              borderRadius: 1.5,
              "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
              "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
              "&.Mui-focused fieldset": {
                borderColor: brandColor,
                boxShadow: `0 0 0 2px ${brandColor}33`,
              },
            },
          }}
          InputLabelProps={{
            sx: {
              color: perlColors.textSecondary,
              fontSize: "0.85rem",
              letterSpacing: "0.3px",
              "&.Mui-focused": { color: perlColors.textPrimary },
            },
          }}
          fullWidth
        />
        <PerlTextField
          label="Printing Coverage"
          type="number"
          value={printingCoverage || ""}
          onChange={handlePrintingCoverageChange}
          placeholder="Enter percentage"
          disabled={isNA}
          InputProps={{
            sx: {
              bgcolor: alpha(perlColors.white, 0.02),
              color: perlColors.textPrimary,
              fontSize: "0.9rem",
              borderRadius: 1.5,
              "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
              "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
              "&.Mui-focused fieldset": {
                borderColor: brandColor,
                boxShadow: `0 0 0 2px ${brandColor}33`,
              },
            },
          }}
          InputLabelProps={{
            sx: {
              color: perlColors.textSecondary,
              fontSize: "0.85rem",
              letterSpacing: "0.3px",
              "&.Mui-focused": { color: perlColors.textPrimary },
            },
          }}
          fullWidth
        />
        <PerlTextField
          label="Ink coverage"
          type="number"
          value={inkCoverage || ""}
          onChange={handleInkCoverageChange}
          placeholder="Enter percentage"
          disabled={isNA}
          InputProps={{
            sx: {
              bgcolor: alpha(perlColors.white, 0.02),
              color: perlColors.textPrimary,
              fontSize: "0.9rem",
              borderRadius: 1.5,
              "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
              "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
              "&.Mui-focused fieldset": {
                borderColor: brandColor,
                boxShadow: `0 0 0 2px ${brandColor}33`,
              },
            },
          }}
          InputLabelProps={{
            sx: {
              color: perlColors.textSecondary,
              fontSize: "0.85rem",
              letterSpacing: "0.3px",
              "&.Mui-focused": { color: perlColors.textPrimary },
            },
          }}
          fullWidth
        />
      </Box>
      {success && (
        <PerlLabelMedium sx={{ mt: 1, color: "success.main" }}>{success}</PerlLabelMedium>
      )}
    </>
  );
});

PrintingDetails.displayName = "PrintingDetails";

export default PrintingDetails;

